import React, { useState, useEffect } from 'react';
import { Shield, Lock, Unlock, Save, X } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const Network = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [ipRanges, setIpRanges] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/api/users');
      setUsers((res.data.data || []).filter(u => u.role !== 'superadmin'));
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleToggleLock = async (u) => {
    try {
      await api.put(`/api/users/${u.id}`, { ...u, network_locked: !u.network_locked });
      toast.success(`Network ${u.network_locked ? 'unlocked' : 'locked'} for ${u.username}`);
      fetchUsers();
    } catch (err) { toast.error('Failed'); }
  };

  const handleSaveRanges = async () => {
    try {
      const ranges = ipRanges.split('\n').map(s => s.trim()).filter(Boolean);
      await api.put(`/api/users/${editUser.id}`, { ...editUser, allowed_ip_ranges: ranges });
      toast.success('IP ranges saved');
      setEditUser(null);
      fetchUsers();
    } catch (err) { toast.error('Failed to save'); }
  };

  const filtered = filter === 'locked' ? users.filter(u => u.network_locked) :
                   filter === 'open' ? users.filter(u => !u.network_locked) : users;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Network Lock</div>
          <div className="page-subtitle">Control which users can only access within specific IP ranges</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-blue)' }}>{users.length}</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Total Users</div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-red)' }}>{users.filter(u => u.network_locked).length}</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Network Locked</div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--accent-teal)' }}>{users.filter(u => !u.network_locked).length}</div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Open Access</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {['all','locked','open'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className="btn" style={{
            padding: '8px 16px', fontSize: '13px', textTransform: 'capitalize',
            background: filter === f ? 'var(--accent-blue)' : 'var(--bg-card)',
            color: filter === f ? 'white' : 'var(--text-secondary)',
            border: '1px solid var(--border)'
          }}>{f}</button>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Network Status</th>
                <th>Allowed IP Ranges</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id}>
                  <td style={{ fontWeight: '500' }}>{u.full_name || u.username}</td>
                  <td><span className="badge badge-blue" style={{ textTransform: 'capitalize' }}>{u.role}</span></td>
                  <td>
                    {u.network_locked
                      ? <span className="badge badge-danger"><Lock size={11} style={{ marginRight: '4px' }} />Locked</span>
                      : <span className="badge badge-success"><Unlock size={11} style={{ marginRight: '4px' }} />Open</span>}
                  </td>
                  <td style={{ fontSize: '13px', fontFamily: 'monospace' }}>
                    {Array.isArray(u.allowed_ip_ranges) && u.allowed_ip_ranges.length > 0
                      ? u.allowed_ip_ranges.join(', ')
                      : <span style={{ color: 'var(--text-secondary)' }}>No restrictions</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleToggleLock(u)} className={`btn ${u.network_locked ? 'btn-success' : 'btn-danger'}`} style={{ padding: '6px 12px', fontSize: '13px' }}>
                        {u.network_locked ? <><Unlock size={14} />Unlock</> : <><Lock size={14} />Lock</>}
                      </button>
                      <button onClick={() => { setEditUser(u); setIpRanges(Array.isArray(u.allowed_ip_ranges) ? u.allowed_ip_ranges.join('\n') : ''); }}
                        className="btn btn-secondary" style={{ padding: '6px 12px', fontSize: '13px' }}>
                        <Shield size={14} /> IP Ranges
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editUser && (
        <div className="modal-overlay" onClick={() => setEditUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">IP Ranges — {editUser.full_name || editUser.username}</div>
              <button className="close-btn" onClick={() => setEditUser(null)}><X size={20} /></button>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
              Enter allowed IP ranges (CIDR notation), one per line. Leave empty to allow all IPs.
            </p>
            <div className="form-group">
              <label>Allowed IP Ranges</label>
              <textarea className="form-control" rows={6} value={ipRanges} onChange={e => setIpRanges(e.target.value)}
                placeholder={'103.180.0.0/16\n202.65.14.0/24\n157.15.0.0/24'} style={{ fontFamily: 'monospace' }} />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEditUser(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveRanges}><Save size={16} />Save Ranges</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Network;
